import { IconDownload, IconSearch, IconX } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useEffect, useState, useMemo } from "react";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";

// Components
import NormalSearch from "../../components/common/NormalSearch";
import DropdownFilter from "../../components/common/DropdownFilter";
import ColumnsFilter from "../../components/common/ColumnsFilter";
import ListView from "../../components/common/ListView";
import MessageModal from "../../components/MessageModal";
import EntriesSelector from "../../components/common/EntriesSelector";
import NoDataFound from "../../components/common/NoDataFound";
import TableSkeleton from "../../components/skeleton/TableSkeleton";
import Pagination from "../../components/common/Pagination";

// Hooks & Helpers
import useDebounce from "../../utilities/useDebounce";
import { useUpdateProductItemStatus } from "../../apis/productApis";
import { useFetchOrderAllocationData } from "../../apis/orderAllocationApis";
import { formatDateTime } from "../../utilities/helper";
import { useLanguage } from "../../context/LanguageContext";

interface OrderAllocationProps {
  setHeaderButtons?: (node: React.ReactNode) => void;
}

export default function OrderAllocation({ setHeaderButtons }: OrderAllocationProps = {}) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [pageNo, setPageNo] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [searchFor, setSearchFor] = useState<string>("");
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [actionModal, setActionModal] = useState<boolean>(false);
  const [showSearch, setShowSearch] = useState<boolean>(false);

  const handleExportXLSX = () => {
    const dataToExport = data?.orderAllocations || [];
    if (dataToExport.length === 0) {
      toast.error(t("No data available to export"));
      return;
    }
    const exportData = dataToExport.map((row: any, idx: number) => ({
      [t("S/N")]: idx + 1,
      [t("AWB Number")]: row.awbNumber || "",
      [t("Item Name")]: row.itemName || "",
      [t("SKU")]: row.sku || "",
      [t("Quantity")]: row.quantity || 0,
      [t("Warehouse")]: row.warehouseName || "",
      [t("Location Type")]: row.locationType || "BIN",
      [t("Location")]: row.locationName || "",
      [t("Date & Time")]: row.createdAt ? new Date(row.createdAt).toLocaleString() : "",
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t("Order Allocations"));
    XLSX.writeFile(wb, "order_allocations_export.xlsx");
    toast.success(t("Exported successfully!"));
  };

  const { mutate: updateProductStatus, isPending: statusPending } = useUpdateProductItemStatus();

  const confirmUpdateStatus = async () => {
    const newStatus = selectedItem?.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    updateProductStatus(
      { id: selectedItem?.id, status: newStatus },
      {
        onSuccess: () => {
          refetch();
          toast.success(t("Status updated successfully"));
          setActionModal(false);
        },
        onError: (error) => {
          toast.error(`${error}`);
        },
      }
    );
  };

  const debouncedSearchFor = useDebounce(searchFor, 550);
  const { data, isLoading, isFetching, refetch } = useFetchOrderAllocationData(
    pageNo,
    limit,
    debouncedSearchFor
  );

  useEffect(() => {
    refetch();
  }, [pageNo, limit, debouncedSearchFor]);

  useEffect(() => {
    if (setHeaderButtons) {
      setHeaderButtons(
        <div className="flex items-center flex-wrap gap-2 bg-white/70 backdrop-blur-sm px-4 py-1.5 rounded-xl border border-slate-200 shadow-sm text-[11px] select-none font-bold min-h-8">
          <div className="flex items-center gap-1.5">
            <span className="text-slate-500">
              {t("Total")}:
            </span>
            <span className="text-blue-700 bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded">
              {data?.total ?? 0}
            </span>
          </div>
        </div>
      );
    }
  }, [data?.total, setHeaderButtons, t]);

  const handlePaginationFetch = (page: number) => {
    setPageNo(page);
  };

  const [columnsData, setColumnsData] = useState([
    { text: "S/N", value: "sn", isShow: true },
    { text: "AWB Number", value: "awbNumber", isShow: true },
    { text: "Item", value: "item", isShow: true },
    { text: "Qty.", value: "qty", isShow: true },
    { text: "Warehouse", value: "warehouse", isShow: true },
    { text: "Location Type", value: "locationType", isShow: true },
    { text: "Location", value: "shelveID", isShow: true },
    { text: "Date & Time", value: "createdAt", isShow: true },
  ]);

  const setHeadings = (value: string | undefined, checked: boolean) => {
    setColumnsData((prev) =>
      prev.map((col) => (col.value === value ? { ...col, isShow: checked } : col))
    );
  };



  const columns = useMemo(() => {
    const dynamicCols = columnsData
      .filter((c) => c.isShow)
      .map((col) => {
        switch (col.value) {
          case "sn":
            return {
              label: t("S/N"),
              render: (_: any, i: number) => (pageNo - 1) * limit + (i + 1),
            };
          case "awbNumber":
            return {
              label: t("AWB Number"),
              render: (row: any) => (
                <div
                  className="flex cursor-pointer hover:text-blue-800 hover:underline"
                  onClick={() => navigate(`/orders/${row?.awbNumber}`)}
                >
                  <span>{row?.awbNumber || ""}</span>
                </div>
              ),
            };
          case "item":
            return {
              label: t("Item"),
              render: (c: any) => (
                <div className="flex items-center space-x-3 truncate max-w-xs">
                  <img
                    alt=""
                    src={c?.image}
                    className="size-12 rounded-md object-cover object-center"
                  />
                  <div>
                    <p title={c.itemName}>{c.itemName}</p>
                    <p>{c.sku}</p>
                  </div>
                </div>
              ),
            };
          case "qty":
            return {
              label: t("Qty."),
              render: (c: any) => c.quantity,
            };
          case "warehouse":
            return {
              label: t("Warehouse"),
              render: (c: any) => c.warehouseName,
            };
          case "locationType":
            return {
              label: t("Location Type"),
              render: (c: any) => t(c.locationType || "BIN"),
            };
          case "shelveID":
            return {
              label: t("Location"),
              render: (c: any) => (
                <div className="flex flex-col items-center space-x-3">
                  <img
                    alt=""
                    src={c?.binLocationBarcode || "/img/bin-location-placeholder.png"}
                    className="object-cover object-center w-full h-11"
                  />
                  <span className="text-[13px] font-semibold text-[#131327]/70">
                    {c.locationName ? c.locationName : ""}
                  </span>
                </div>
              ),
            };
          case "createdAt":
            return {
              label: t("Date & Time"),
              render: (c: any) => formatDateTime(c.createdAt),
            };
          default:
            return { label: "", render: () => null };
        }
      });

    return dynamicCols;
  }, [columnsData, data, pageNo, limit]);

  if (isLoading) {
    return (
      <div className="pt-2">
        <TableSkeleton
          columns={columnsData.filter((c) => c.isShow).map((c) => t(c.text))}
          rows={6}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="bg-[#F7F6F2] flex items-center justify-end px-6 py-3.5 w-full rounded-tr-[40px]">
        <div className="flex items-center gap-2">

            {/* Sliding Search */}
            <div className="relative flex items-center">
              <div
                onClick={() => {
                  if (!showSearch) {
                    setShowSearch(true);
                  }
                }}
                className={`flex items-center transition-all duration-300 ease-in-out border border-slate-200 rounded-lg bg-white shadow-sm h-8 ${
                  showSearch ? "w-64 px-2 cursor-default" : "w-8 justify-center cursor-pointer"
                }`}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowSearch(!showSearch);
                  }}
                  className="flex items-center justify-center text-slate-700 focus:outline-none flex-shrink-0"
                  title={t("Search")}
                >
                  <IconSearch size={17} stroke={2} />
                </button>
                {showSearch && (
                  <>
                    <input
                      type="text"
                      value={searchFor}
                      onChange={(e) => {
                        setSearchFor(e.target.value);
                        setPageNo(1);
                      }}
                      placeholder={t("Search...")}
                      autoFocus
                      className="flex-1 ml-2 bg-transparent text-xs text-slate-800 placeholder-slate-400 outline-none border-none"
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (searchFor) {
                          setSearchFor("");
                        } else {
                          setShowSearch(false);
                        }
                        setPageNo(1);
                      }}
                      className="flex items-center justify-center p-0.5 ml-1 text-slate-400 hover:text-slate-600 focus:outline-none flex-shrink-0"
                      title={t("Clear & Close")}
                    >
                      <IconX size={14} stroke={2} />
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Export */}
            <button
              onClick={handleExportXLSX}
              className="flex items-center justify-center h-8 w-8 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg transition shadow-sm active:scale-95 duration-150"
              title={t("Export to Excel")}
            >
              <IconDownload size={17} stroke={2} />
            </button>

            {/* Entries Selector */}
            {data && data.total > 10 && (
              <EntriesSelector
                value={limit}
                options={[
                  10,
                  ...(data.total > 10 ? [20] : []),
                  ...(data.total > 20 ? [30] : []),
                  ...(data.total > 50 ? [50] : []),
                  ...(data.total > 100 ? [100] : []),
                ]}
                onChange={(val) => {
                  setLimit(val);
                  setPageNo(1);
                }}
              />
            )}

            {/* Columns Hide/Show Filter */}
            <ColumnsFilter columnsData={columnsData.map(c => ({ ...c, text: t(c.text) }))} setHeadings={setHeadings} />

          </div>
        </div>

        <div>
          {isLoading ? (
            <TableSkeleton
              columns={columnsData.filter((c) => c.isShow).map((c) => t(c.text))}
              rows={6}
            />
          ) : data?.orderAllocations?.length ? (
            <ListView
              data={data?.orderAllocations}
              columns={columns}
              mainDivClassName="bg-[#F7F6F2] rounded-br-3xl"
              mailDivClassName="p-0 rounded-br-3xl"
            />
          ) : (
            <NoDataFound />
          )}
        </div>

        <div className="flex justify-center items-center py-4">
          {data?.totalPages > 1 && (
            <Pagination
              currentPage={pageNo}
              totalPages={data?.totalPages}
              fetchData={handlePaginationFetch}
            />
          )}
        </div>

      <MessageModal
        open={actionModal}
        onClose={() => setActionModal(false)}
        title={t("Confirmation Required")}
        subtitle={t("Are you sure you want to perform this action?")}
        img={selectedItem?.status === "ACTIVE" ? "/img/inactive.svg" : "/img/active.svg"}
        showFooter={true}
        closeButtonText={t("Cancel")}
        actionButtonText={t("Confirm")}
        actionButtonOnClick={confirmUpdateStatus}
        isPending={statusPending}
        size="md"
      />
    </div>
  );
}